// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/skill/SkillListPage.jsx
// 📝 Purpose:
//   Skill Master – List Page
//   - Gradient header
//   - KPI bar
//   - Search + status filter
//   - Table view
//   - Create / Edit Drawer
//   - SAME PATTERN AS MachineGroupListPage
// ======================================================================

import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";

import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import SkillDrawer from "./SkillDrawer";
//import SkillKpiBar from "../../../components/skill/SkillKpiBar";
import SkillKpiBar from "../../../components/engineering/skill/SkillKpiBar";

// ----------------------------------------------------------------------
export default function SkillListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // Load skills
  // ------------------------------------------------------------
  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/skills"
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load skills", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Filtering
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.skill_code?.toLowerCase().includes(q) ||
      r.skill_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active) ||
      (status === "INACTIVE" && !r.is_active);

    return matchesSearch && matchesStatus;
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Skills</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Define workforce skills and capabilities
          </div>
        </div>

        {permissions?.SKILL_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Skill
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <SkillKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadSkills}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 160 }}>Skill Code</th>
                <th>Skill Name</th>
                <th style={{ width: 160 }}>Level Required</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center text-muted"
                  >
                    No skills found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.skill_code}</td>
                  <td>{r.skill_name}</td>
                  <td>
                    {r.level_required ? "Yes" : "No"}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {permissions?.SKILL_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <SkillDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={loadSkills}
      />
    </div>
  );
}
